/**
 * Service-worker registration helper with auto-update prompt.
 *
 * Usage in App.js:
 *   import { registerSW, onUpdateAvailable } from './lib/sw';
 *   useEffect(() => {
 *     registerSW();
 *     return onUpdateAvailable(setNewVersionWaiting);
 *   }, []);
 */

let waitingWorker = null;
let listeners = new Set();

function notifyListeners(reg) {
  waitingWorker = reg.waiting;
  for (const cb of listeners) {
    try { cb(reg); } catch (e) { console.warn('[SW] listener error', e); }
  }
}

export async function registerSW() {
  if (!('serviceWorker' in navigator)) return null;
  if (process.env.NODE_ENV !== 'production') return null; // CRA only ships SW in prod
  try {
    const reg = await navigator.serviceWorker.register('/service-worker.js');

    // 1) Initial state: a new SW is already waiting (page reloaded mid-update)
    if (reg.waiting && navigator.serviceWorker.controller) {
      notifyListeners(reg);
    }

    // 2) A new SW starts installing — listen for the moment it finishes
    reg.addEventListener('updatefound', () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          notifyListeners(reg);
        }
      });
    });

    // 3) Once user accepts and the new SW takes control, reload the page so
    //    fresh shell/assets are used immediately.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Avoid infinite reload loops in dev tools "Update on reload" mode
      if (window.__matbakhiReloaded) return;
      window.__matbakhiReloaded = true;
      window.location.reload();
    });

    // Periodically check for updates (every 30 min)
    setInterval(() => { reg.update().catch(() => {}); }, 30 * 60 * 1000);

    return reg;
  } catch (e) {
    console.warn('[SW] registration failed', e);
    return null;
  }
}

/** Subscribe to "new version ready" events. Returns an unsubscribe function. */
export function onUpdateAvailable(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Tell the waiting SW to skipWaiting → triggers controllerchange → reload. */
export function applyUpdate() {
  if (waitingWorker) waitingWorker.postMessage({ type: 'SKIP_WAITING' });
}
